import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

/** צריך להתאים ל duration שבמחלקות, אחרת המגש ייעלם באמצע התנועה. */
const EXIT_MS = 180;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * מגש שנפתח מצד המסך, בלי לעזוב את מה שמאחוריו.
 *
 * לעומת דיאלוג: דיאלוג עוצר כדי לשאול שאלה אחת, והמגש הוא מקום
 * עבודה. הוא נפתח לצד הדבר שנערך, כדי שההקשר יישאר על המסך —
 * בלוח הסידור רואים את שאר השבוע בזמן שמשנים משמרת אחת.
 *
 * ב RTL הצד הלוגי end הוא שמאל, ושם המגש יושב.
 *
 * התנועה אינה קישוט: מגש שמופיע ונעלם בבת אחת נראה כמו החלפת
 * מסך, והתנועה היא מה שאומר "זה נפתח מכאן, וזה עדיין אותו דף".
 * לכן גם היציאה מונפשת, והרכיב נשאר מורכב עד שהיא נגמרת.
 */
export function Drawer({
  title,
  subtitle,
  children,
  footer,
  onClose,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /**
   * מקבל את הסגירה המונפשת. כפתור שקורא ל onClose הישיר יקפיץ
   * את המגש מהמסך באמצע התנועה.
   */
  footer?: ReactNode | ((close: () => void) => ReactNode);
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  // הכניסה רצה מעצמה מ CSS. רק היציאה צריכה JS, כי צריך להשאיר
  // את הרכיב מורכב עד שהיא נגמרת.
  const close = useCallback(() => {
    if (isLeaving) return;
    setIsLeaving(true);
    window.setTimeout(onClose, prefersReducedMotion() ? 0 : EXIT_MS);
  }, [isLeaving, onClose]);

  // המיקוד נכנס למגש, אחרת Tab ממשיך לדפדף בדף שמאחור
  useEffect(() => {
    const first = panelRef.current?.querySelector<HTMLElement>(
      'input, select, textarea, button',
    );
    first?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <div className="fixed inset-0 z-40 flex">
      <button
        type="button"
        aria-label="סגירת המגש"
        onClick={close}
        className={`grow cursor-default border-0 bg-charcoal/40 p-0 motion-reduce:animate-none ${
          isLeaving ? 'animate-scrim-out' : 'animate-scrim-in'
        }`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`grid w-full max-w-sm grid-rows-[auto_1fr_auto] overflow-hidden border-s border-line bg-surface shadow-lg motion-reduce:animate-none ${
          isLeaving ? 'animate-drawer-out' : 'animate-drawer-in'
        }`}
      >
        <div className="flex items-start justify-between gap-2 border-b border-line p-4">
          <div className="grid gap-0.5">
            <h2 className="m-0 font-display text-display-m font-semibold text-ink">{title}</h2>
            {subtitle && <p className="m-0 text-body-sm text-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            aria-label="סגירה"
            onClick={close}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto p-4">{children}</div>

        {footer && (
          <div className="flex flex-wrap gap-2 border-t border-line p-4">
            {typeof footer === 'function' ? footer(close) : footer}
          </div>
        )}
      </div>
    </div>
  );
}
